/**
 * FeaturesContext — the single source of truth for feature flags on the FE.
 *
 * Design:
 *   · Fetched ONCE on app boot (after login) via GET /api/features/me
 *   · Stored in an in-memory Map for O(1) lookups (`useFeature("id")` is
 *     as cheap as a boolean check — no re-renders, no per-page network)
 *   · Refetched only on logout/login or when the admin re-applies features
 *     to the user's company (rare)
 *
 * Public API:
 *   · `useFeature(id)`         — boolean; unknown ids default to true so
 *                                a typo never breaks an existing page.
 *   · `useAnyFeature(ids[])`   — true if any of the ids are enabled
 *   · `filterByFeature(items, getId)` — filter an array of nav items by their
 *                                        `feature` id.
 *   · `<Feature id="..."/>`    — declarative gate: `<Feature id="foo"><Btn/></Feature>`
 *
 * The user-facing name is deliberately terse ("useFeature") — this hook
 * gets sprinkled everywhere and we want it to disappear into the code.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, getImpersonatedCompanyId, setImpersonatedCompanyId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FeaturesContext = createContext({
    features: {},
    loading: false,
    ready: false,
    refresh: () => {},
});

export function FeaturesProvider({ children }) {
    const { user, refresh: refreshAuth } = useAuth() || {};
    // Use a ref-mirrored state so `useFeature` reads without waiting for the
    // set-state to flush on the first render after login.
    const [features, setFeatures] = useState({});
    // Impersonation — TRUE impersonation. When active, the API interceptor
    // adds ``X-Impersonate-Company`` on every request and the backend swaps
    // the effective user to the target company's owner (read-only). We
    // mirror the company_id + name in state so the banner + FeatureManagement
    // page can react. Persisted in localStorage via api.js helpers so a
    // page refresh keeps preview mode on.
    const [impersonation, setImpersonationState] = useState(() => {
        const cid = typeof window !== "undefined" ? getImpersonatedCompanyId() : null;
        return cid ? { company_id: cid, company_name: null } : null;
    });
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const inFlight = useRef(false);

    const load = useCallback(async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setLoading(true);
        try {
            const { data } = await api.get("/features/me");
            setFeatures(data?.features || {});
            setReady(true);
        } catch {
            // Fail open — don't lock users out of the whole product if the
            // features endpoint has a hiccup. Registry defaults still apply
            // per-check via the fallback in `useFeature`.
            setFeatures({});
            setReady(true);
        } finally {
            setLoading(false);
            inFlight.current = false;
        }
    }, []);

    useEffect(() => {
        // Load when auth resolves for the first time OR when the user id
        // changes (logout → login). Reset ready so downstream hooks can gate.
        if (user?.id) {
            setReady(false);
            load();
        } else {
            setFeatures({});
            setReady(false);
        }
    }, [user?.id, load]);

    /**
     * Public API. Two call shapes:
     *   setImpersonation(null)                       — exit preview mode
     *   setImpersonation({ company_id, company_name }) — start preview mode
     *
     * When starting we:
     *   1. Persist the company_id in localStorage so the interceptor picks
     *      it up on every subsequent request (including any triggered by
     *      React re-renders between now and refreshAuth completing).
     *   2. Refresh the current user via /auth/me so AuthContext.user now
     *      reflects the impersonated company_id + role.
     *   3. Reload the resolved feature map (/features/me) for the target.
     */
    const setImpersonation = useCallback(async (payload) => {
        if (!payload) {
            // Clear localStorage FIRST so the interceptor doesn't attach the
            // header on the exit audit call (which requires the real admin
            // session to satisfy require_role("admin")).
            setImpersonatedCompanyId(null);
            try { await api.post("/admin/impersonate/exit"); } catch { /* audit-only */ }
            setImpersonationState(null);
            try { await refreshAuth?.(); } catch { /* ignore */ }
            await load();
            return;
        }
        const { company_id, company_name } = payload;
        if (!company_id) return;
        try {
            // Hit the audit endpoint FIRST — it validates the company exists
            // and gives us the owner name. Doing it before setting the
            // header prevents leaking impersonation state on an invalid id.
            const { data } = await api.post(`/admin/impersonate/${company_id}`);
            setImpersonatedCompanyId(company_id);
            setImpersonationState({
                company_id,
                company_name: data?.company?.name || company_name || null,
                owner: data?.owner || null,
            });
            try { await refreshAuth?.(); } catch { /* ignore */ }
            await load();
        } catch (e) {
            // On failure, ensure we don't leave header state polluted.
            setImpersonatedCompanyId(null);
            setImpersonationState(null);
            throw e;
        }
    }, [load, refreshAuth]);

    const value = useMemo(() => ({
        features,
        loading,
        ready,
        refresh: load,
        impersonating: !!impersonation,
        impersonation,
        setImpersonation,
    }), [features, loading, ready, load, impersonation, setImpersonation]);

    return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}

/** Read a single feature flag. Unknown ids return true so a stray typo
 *  never accidentally hides a route the user should see. */
export function useFeature(id) {
    const { features } = useContext(FeaturesContext);
    if (id == null) return true;
    // If the id isn't in the resolved map at all, default to true. If it
    // IS in the map and explicitly false → hide.
    if (Object.prototype.hasOwnProperty.call(features, id)) {
        return !!features[id];
    }
    return true;
}

/** Return true if any of the given feature ids are enabled — handy for
 *  showing a nav group when at least one child is visible. */
export function useAnyFeature(ids = []) {
    const { features } = useContext(FeaturesContext);
    return ids.some((id) => {
        if (Object.prototype.hasOwnProperty.call(features, id)) return !!features[id];
        return true;
    });
}

/** Filter a list of items by a feature id extracted via `getId(item)`.
 *  Used to strip nav items from the sidebar in one pass. */
export function useFilterByFeature() {
    const { features } = useContext(FeaturesContext);
    return useCallback((items, getId) => {
        return items.filter((it) => {
            const id = getId ? getId(it) : it?.feature;
            if (!id) return true;
            if (Object.prototype.hasOwnProperty.call(features, id)) return !!features[id];
            return true;
        });
    }, [features]);
}

/** Declarative gate: `<Feature id="foo"><Button/></Feature>` renders
 *  children only when the flag is enabled. */
export function Feature({ id, children, fallback = null }) {
    const on = useFeature(id);
    return on ? children : fallback;
}

/** Escape hatch for admin dashboards that need the full map. */
export function useFeatures() {
    return useContext(FeaturesContext);
}
